import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';

const SearchFlowProcess = () => {
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [queryInput, setQueryInput] = useState('Comment traiter un chèque impayé selon BEAC?');
  const [progressWidth, setProgressWidth] = useState('0%');
  const [showAnalysisResults, setShowAnalysisResults] = useState(false);
  const [showFusionResults, setShowFusionResults] = useState(false);
  const [showGenerationResults, setShowGenerationResults] = useState(false);
  const [methodsVisible, setMethodsVisible] = useState([false, false, false, false]);
  const [resultsVisible, setResultsVisible] = useState([false, false, false]);
  const [timelineBarsVisible, setTimelineBarsVisible] = useState([false, false, false, false, false]);

  // Animate info cards on scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });

    const cards = document.querySelectorAll('.info-card');
    cards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      card.style.transition = 'all 0.6s ease-out';
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const setExampleQuery = (query) => {
    setQueryInput(query);
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const resetSearch = () => {
    setActiveStep(0);
    setProgressWidth('0%');
    setShowAnalysisResults(false);
    setShowFusionResults(false);
    setShowGenerationResults(false);
    setMethodsVisible([false, false, false, false]);
    setResultsVisible([false, false, false]);
    setTimelineBarsVisible([false, false, false, false, false]);
  };

  const simulateStep = async (stepNumber, duration, progressTarget) => {
    setActiveStep(stepNumber);
    setProgressWidth(`${progressTarget}%`);
    
    if (stepNumber === 1) {
      await delay(200);
      setShowAnalysisResults(true);
    } else if (stepNumber === 2) {
      // Animate method cards
      for (let i = 0; i < 4; i++) {
        await delay(150);
        setMethodsVisible(prev => {
          const newVisible = [...prev];
          newVisible[i] = true;
          return newVisible;
        });
        await delay(50);
      }
    } else if (stepNumber === 3) {
      await delay(200);
      setShowFusionResults(true);
      // Animate result items
      for (let i = 0; i < 3; i++) {
        await delay(100);
        setResultsVisible(prev => {
          const newVisible = [...prev];
          newVisible[i] = true;
          return newVisible;
        });
      }
    } else if (stepNumber === 4) {
      await delay(300);
      setShowGenerationResults(true);
    }
    
    await delay(duration);
  };

  const showTimeline = () => {
    setTimelineBarsVisible([false, false, false, false, false]);
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        setTimelineBarsVisible(prev => {
          const newVisible = [...prev];
          newVisible[i] = true;
          return newVisible;
        });
      }, i * 100);
    }
  };

  const startSearch = async () => {
    if (searchInProgress) return;
    
    setSearchInProgress(true);
    resetSearch();
    
    await simulateStep(1, 50, 10);
    await simulateStep(2, 800, 40);
    await simulateStep(3, 200, 60);
    await simulateStep(4, 950, 100);
    
    showTimeline();
    setSearchInProgress(false);
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
            overflow-x: hidden;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Interactive Demo Section */
        .demo-section {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            padding: 3rem;
            margin-bottom: 4rem;
            position: relative;
            overflow: hidden;
        }
        
        .query-input-section {
            text-align: center;
            margin-bottom: 3rem;
        }
        
        .query-input {
            width: 100%;
            max-width: 600px;
            padding: 1.2rem 1.5rem;
            background: rgba(255, 255, 255, 0.05);
            border: 2px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            color: white;
            font-size: 1.1rem;
            transition: all 0.3s;
        }
        
        .query-input:focus {
            outline: none;
            border-color: rgba(102, 126, 234, 0.5);
            background: rgba(255, 255, 255, 0.08);
        }
        
        .query-input::placeholder {
            color: #666;
        }
        
        .search-button {
            margin-top: 1rem;
            padding: 1rem 3rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 1.1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s;
        }
        
        .search-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        .search-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
        
        /* Process Flow */
        .process-flow {
            margin: 3rem 0;
            position: relative;
        }
        
        .flow-step {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin-bottom: 2rem;
            opacity: 0.3;
            transform: translateX(-20px);
            transition: all 0.5s;
        }
        
        .flow-step.active {
            opacity: 1;
            transform: translateX(0);
            border-color: rgba(102, 126, 234, 0.5);
            background: rgba(102, 126, 234, 0.05);
        }
        
        .flow-step.completed {
            opacity: 0.8;
            border-color: rgba(103, 232, 169, 0.5);
        }
        
        .step-header {
            display: flex;
            align-items: center;
            gap: 1.5rem;
            margin-bottom: 1.5rem;
        }
        
        .step-number {
            width: 50px;
            height: 50px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            font-weight: bold;
        }
        
        .step-number.completed {
            background: linear-gradient(135deg, #67e8a9 0%, #48bb78 100%);
        }
        
        .step-title {
            flex: 1;
        }
        
        .step-title h3 {
            font-size: 1.5rem;
            margin-bottom: 0.3rem;
        }
        
        .step-duration {
            color: #888;
            font-size: 0.9rem;
        }
        
        .step-content {
            margin-left: 65px;
        }
        
        /* Method Cards in Steps */
        .methods-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 1rem;
            margin-top: 1.5rem;
        }
        
        .method-card {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 1.5rem;
            text-align: center;
            opacity: 0;
            transform: scale(0.9);
            transition: all 0.3s;
        }
        
        .method-card.show {
            opacity: 1;
            transform: scale(1);
        }
        
        .method-card.active {
            border-color: rgba(102, 126, 234, 0.5);
            background: rgba(102, 126, 234, 0.1);
            animation: pulse 1s ease-in-out;
        }
        
        @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
        }
        
        .method-icon {
            font-size: 2rem;
            margin-bottom: 0.5rem;
        }
        
        .method-name {
            font-weight: bold;
            margin-bottom: 0.3rem;
        }
        
        .method-percentage {
            font-size: 1.5rem;
            color: #667eea;
            font-weight: bold;
        }
        
        /* Results Preview */
        .results-preview {
            margin-top: 2rem;
            display: none;
        }
        
        .results-preview.show {
            display: block;
            animation: fadeIn 0.5s ease;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        .result-item {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 1rem;
            margin-bottom: 0.5rem;
            display: flex;
            align-items: center;
            gap: 1rem;
            opacity: 0;
            transform: translateX(-20px);
            transition: all 0.3s;
        }
        
        .result-item.show {
            opacity: 1;
            transform: translateX(0);
        }
        
        .result-score {
            font-size: 1.2rem;
            font-weight: bold;
            color: #67e8a9;
        }
        
        .result-title {
            flex: 1;
        }
        
        .result-method {
            font-size: 0.8rem;
            color: #667eea;
            background: rgba(102, 126, 234, 0.1);
            padding: 0.2rem 0.6rem;
            border-radius: 4px;
        }
        
        /* Progress Bar */
        .progress-bar-container {
            width: 100%;
            height: 8px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 4px;
            overflow: hidden;
            margin: 2rem 0;
        }
        
        .progress-bar {
            height: 100%;
            background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
            width: 0%;
            transition: width 0.3s ease;
            position: relative;
        }
        
        .progress-bar::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            width: 50px;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3));
            animation: shimmer 1.5s ease-in-out infinite;
        }
        
        @keyframes shimmer {
            0% { transform: translateX(-50px); }
            100% { transform: translateX(50px); }
        }
        
        /* Timeline Visualization */
        .timeline-viz {
            margin: 3rem 0;
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
        }
        
        .timeline-header {
            text-align: center;
            margin-bottom: 2rem;
        }
        
        .timeline-graph {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            height: 200px;
            margin-bottom: 1rem;
        }
        
        .timeline-bar {
            flex: 1;
            background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
            border-radius: 8px 8px 0 0;
            margin: 0 0.5rem;
            position: relative;
            opacity: 0;
            transform: scaleY(0);
            transform-origin: bottom;
            transition: all 0.5s;
        }
        
        .timeline-bar.show {
            opacity: 1;
            transform: scaleY(1);
        }
        
        .timeline-value {
            position: absolute;
            top: -30px;
            left: 50%;
            transform: translateX(-50%);
            font-weight: bold;
            color: #667eea;
        }
        
        .timeline-labels {
            display: flex;
            justify-content: space-between;
        }
        
        .timeline-label {
            flex: 1;
            text-align: center;
            font-size: 0.9rem;
            color: #888;
            padding: 0 0.5rem;
        }
        
        /* Example Queries */
        .example-queries {
            display: flex;
            gap: 1rem;
            justify-content: center;
            margin-bottom: 2rem;
            flex-wrap: wrap;
        }
        
        .example-query {
            padding: 0.5rem 1rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 0.9rem;
        }
        
        .example-query:hover {
            background: rgba(102, 126, 234, 0.1);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        /* Info Cards */
        .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2rem;
            margin: 3rem 0;
        }
        
        .info-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            transition: all 0.3s;
        }
        
        .info-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .info-icon {
            width: 50px;
            height: 50px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            margin-bottom: 1rem;
        }
        
        .info-title {
            font-size: 1.2rem;
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
        
        .info-description {
            color: #888;
            line-height: 1.5;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .methods-grid {
                grid-template-columns: repeat(2, 1fr);
            }
            
            .timeline-graph {
                height: 150px;
            }
            
            .example-queries {
                flex-direction: column;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Processus de Recherche</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Processus de Recherche</h1>
          <p className="page-subtitle">Visualisation interactive du flux de recherche RAG Hybride</p>
        </div>
        
        {/* Interactive Demo */}
        <div className="demo-section">
          <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Démonstration Interactive</h2>
          
          <div className="example-queries">
            <div className="example-query" onClick={() => setExampleQuery('Comment traiter un chèque impayé selon BEAC?')}>
              Chèque impayé BEAC
            </div>
            <div className="example-query" onClick={() => setExampleQuery('Erreur ORA-12154 dans module paiement')}>
              Erreur technique
            </div>
            <div className="example-query" onClick={() => setExampleQuery('Procédure ouverture compte entreprise')}>
              Procédure métier
            </div>
          </div>
          
          <div className="query-input-section">
            <input 
              type="text" 
              className="query-input"
              placeholder="Entrez votre question..."
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
            />
            <br />
            <button 
              className="search-button" 
              onClick={startSearch}
              disabled={searchInProgress}
            >
              {searchInProgress ? 'Recherche en cours...' : 'Lancer la Recherche'}
            </button>
          </div>
          
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: progressWidth }}></div>
          </div>
          
          {/* Process Steps */}
          <div className="process-flow">
            {/* Step 1: Analyse */}
            <div className={`flow-step ${activeStep >= 1 ? 'active' : ''} ${activeStep > 1 ? 'completed' : ''}`}>
              <div className="step-header">
                <div className={`step-number ${activeStep > 1 ? 'completed' : ''}`}>1</div>
                <div className="step-title">
                  <h3>Analyse de la Requête</h3>
                  <div className="step-duration">~50ms</div>
                </div>
              </div>
              <div className="step-content">
                <p>Extraction des entités, classification de l'intention et enrichissement de la requête.</p>
                <div className={`results-preview ${showAnalysisResults ? 'show' : ''}`}>
                  <p><strong>Intent détecté:</strong> Compliance + Operational</p>
                  <p><strong>Entités:</strong> [chèque, impayé, BEAC]</p>
                  <p><strong>Expansion:</strong> + réglementation bancaire + procédures rejet</p>
                </div>
              </div>
            </div>
            
            {/* Step 2: Recherche Parallèle */}
            <div className={`flow-step ${activeStep >= 2 ? 'active' : ''} ${activeStep > 2 ? 'completed' : ''}`}>
              <div className="step-header">
                <div className={`step-number ${activeStep > 2 ? 'completed' : ''}`}>2</div>
                <div className="step-title">
                  <h3>Recherche Multi-Méthodes</h3>
                  <div className="step-duration">~800ms</div>
                </div>
              </div>
              <div className="step-content">
                <p>Exécution parallèle des 4 méthodes de recherche avec leurs poids respectifs.</p>
                <div className="methods-grid">
                  <div className={`method-card ${methodsVisible[0] ? 'show' : ''} ${activeStep === 2 && methodsVisible[0] ? 'active' : ''}`}>
                    <div className="method-icon">🧠</div>
                    <div className="method-name">Vectorielle</div>
                    <div className="method-percentage">60%</div>
                  </div>
                  <div className={`method-card ${methodsVisible[1] ? 'show' : ''} ${activeStep === 2 && methodsVisible[1] ? 'active' : ''}`}>
                    <div className="method-icon">🔍</div>
                    <div className="method-name">Keywords</div>
                    <div className="method-percentage">25%</div>
                  </div>
                  <div className={`method-card ${methodsVisible[2] ? 'show' : ''} ${activeStep === 2 && methodsVisible[2] ? 'active' : ''}`}>
                    <div className="method-icon">🕸️</div>
                    <div className="method-name">Graphe</div>
                    <div className="method-percentage">10%</div>
                  </div>
                  <div className={`method-card ${methodsVisible[3] ? 'show' : ''} ${activeStep === 2 && methodsVisible[3] ? 'active' : ''}`}>
                    <div className="method-icon">📋</div>
                    <div className="method-name">Metadata</div>
                    <div className="method-percentage">5%</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Step 3: Fusion */}
            <div className={`flow-step ${activeStep >= 3 ? 'active' : ''} ${activeStep > 3 ? 'completed' : ''}`}>
              <div className="step-header">
                <div className={`step-number ${activeStep > 3 ? 'completed' : ''}`}>3</div>
                <div className="step-title">
                  <h3>Fusion des Résultats</h3>
                  <div className="step-duration">~200ms</div>
                </div>
              </div>
              <div className="step-content">
                <p>Algorithme RRF pour combiner et scorer les résultats de toutes les méthodes.</p>
                <div className={`results-preview ${showFusionResults ? 'show' : ''}`}>
                  <div className={`result-item ${resultsVisible[0] ? 'show' : ''}`}>
                    <span className="result-score">95.2%</span>
                    <span className="result-title">Procédure BEAC n°2023-15 - Traitement chèques impayés</span>
                    <span className="result-method">Vector + BM25</span>
                  </div>
                  <div className={`result-item ${resultsVisible[1] ? 'show' : ''}`}>
                    <span className="result-score">89.7%</span>
                    <span className="result-title">Module: gub_cheque_reject.4gl</span>
                    <span className="result-method">Vector + Graph</span>
                  </div>
                  <div className={`result-item ${resultsVisible[2] ? 'show' : ''}`}>
                    <span className="result-score">84.3%</span>
                    <span className="result-title">Guide Utilisateur - Gestion incidents paiement</span>
                    <span className="result-method">BM25</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Step 4: Re-ranking */}
            <div className={`flow-step ${activeStep >= 4 ? 'active' : ''} ${activeStep > 4 ? 'completed' : ''}`}>
              <div className="step-header">
                <div className={`step-number ${activeStep > 4 ? 'completed' : ''}`}>4</div>
                <div className="step-title">
                  <h3>Re-ranking & Génération</h3>
                  <div className="step-duration">~950ms</div>
                </div>
              </div>
              <div className="step-content">
                <p>Cross-encoder pour affiner le classement et génération de la réponse avec le LLM.</p>
                <div className={`results-preview ${showGenerationResults ? 'show' : ''}`}>
                  <p style={{ background: 'rgba(103, 232, 169, 0.1)', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(103, 232, 169, 0.3)' }}>
                    <strong>Réponse générée:</strong><br />
                    Pour traiter un chèque impayé selon les règles BEAC, suivez la procédure définie dans la directive n°2023-15...
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Timeline Visualization */}
        <div className="timeline-viz">
          <div className="timeline-header">
            <h2>Performance Timeline</h2>
            <p style={{ color: '#888' }}>Temps de traitement par étape (ms)</p>
          </div>
          <div className="timeline-graph">
            <div className={`timeline-bar ${timelineBarsVisible[0] ? 'show' : ''}`} style={{ height: '10%' }}>
              <span className="timeline-value">50</span>
            </div>
            <div className={`timeline-bar ${timelineBarsVisible[1] ? 'show' : ''}`} style={{ height: '80%' }}>
              <span className="timeline-value">800</span>
            </div>
            <div className={`timeline-bar ${timelineBarsVisible[2] ? 'show' : ''}`} style={{ height: '20%' }}>
              <span className="timeline-value">200</span>
            </div>
            <div className={`timeline-bar ${timelineBarsVisible[3] ? 'show' : ''}`} style={{ height: '30%' }}>
              <span className="timeline-value">300</span>
            </div>
            <div className={`timeline-bar ${timelineBarsVisible[4] ? 'show' : ''}`} style={{ height: '65%' }}>
              <span className="timeline-value">650</span>
            </div>
          </div>
          <div className="timeline-labels">
            <div className="timeline-label">Analyse</div>
            <div className="timeline-label">Recherche</div>
            <div className="timeline-label">Fusion</div>
            <div className="timeline-label">Re-ranking</div>
            <div className="timeline-label">Génération</div>
          </div>
        </div>
        
        {/* Info Cards */}
        <div className="info-grid">
          <div className="info-card">
            <div className="info-icon">⚡</div>
            <h3 className="info-title">Recherche Parallèle</h3>
            <p className="info-description">
              Les 4 méthodes de recherche s'exécutent simultanément grâce au multi-threading, 
              réduisant significativement la latence totale.
            </p>
          </div>
          
          <div className="info-card">
            <div className="info-icon">🎯</div>
            <h3 className="info-title">Fusion Intelligente</h3>
            <p className="info-description">
              L'algorithme RRF (Reciprocal Rank Fusion) normalise et combine les scores 
              de chaque méthode selon leur poids configuré.
            </p>
          </div>
          
          <div className="info-card">
            <div className="info-icon">🔄</div>
            <h3 className="info-title">Cache Multi-niveaux</h3>
            <p className="info-description">
              Les embeddings, résultats de recherche et réponses complètes sont mis en cache 
              pour des performances optimales sur les requêtes répétées.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchFlowProcess;